import { SensorLocationType, SensorValueType } from 'src/app/models/constant/sensor';
import { LocationPosition } from 'src/app/models/locationGridMapping';
import { SensorEvent } from 'src/app/models/sensorEvent';
import { SensorValue } from 'src/app/models/sensorValue';
import { tirePositionKey } from 'src/app/shared/pipes/tirePosition';
import { PART_TYPE_KEYS, isTireLocationType } from '../sensor-display';

export type SensorTrend = 'up' | 'down' | 'flat' | null;

export interface SensorReading {
  valueType: string;
  atAlert: number;
  now: number | null;
  nowAt: Date | null;
  /** Direction only — null until the live reading resolves. */
  trend: SensorTrend;
}

export interface SensorGroup {
  sensorId: string;
  location: string;
  name: string;
  partType: string | null;
  readings: SensorReading[];
}

export interface SensorDisplay {
  assetType?: string | null;
  mapping?: LocationPosition[] | null;
  /** Per-vehicle layout grid key → location type; null until the layout resolves, leaving part tags blank. */
  locationTypes?: Map<string, SensorLocationType> | null;
  translate: (key: string) => string;
}

export function sensorGroups(
  snapshot: SensorEvent[] | null | undefined,
  current: SensorEvent[] | null | undefined,
  display: SensorDisplay,
): SensorGroup[] {
  const nowBy = new Map<string, SensorValue>();
  for (const s of current ?? []) {
    for (const v of s.values ?? []) nowBy.set(`${s.sensorId}|${v.sensorValueType}`, v);
  }
  const byId = new Map<string, SensorGroup>();
  const groups: SensorGroup[] = [];
  for (const s of snapshot ?? []) {
    for (const v of s.values ?? []) {
      if (!v.sensorValueType || v.sensorValueType === SensorValueType.ImageLink || typeof v.sensorValue !== 'number') continue;
      let group = byId.get(s.sensorId);
      if (!group) {
        group = { sensorId: s.sensorId, location: s.location, ...describe(s, display), readings: [] };
        byId.set(s.sensorId, group);
        groups.push(group);
      }
      const now = nowBy.get(`${s.sensorId}|${v.sensorValueType}`);
      const nowValue = typeof now?.sensorValue === 'number' ? now.sensorValue : null;
      group.readings.push({
        valueType: v.sensorValueType,
        atAlert: v.sensorValue,
        now: nowValue,
        nowAt: now?.dataLastReceived ?? null,
        trend: nowValue === null ? null : nowValue > v.sensorValue ? 'up' : nowValue < v.sensorValue ? 'down' : 'flat',
      });
    }
  }
  return groups.sort((a, b) => {
    const ka = sortKey(a.location, display);
    const kb = sortKey(b.location, display);
    return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
  });
}

function describe(sensor: SensorEvent, display: SensorDisplay): { name: string; partType: string | null } {
  const key = tirePositionKey(sensor.location, display.assetType);
  const name = key ? display.translate(key)
    : sensor.description ? `${sensor.description} ${display.translate('@sensor')}`
    : display.mapping?.find(l => l.location === sensor.location)?.description || sensor.location || '';
  const partTypeKey = PART_TYPE_KEYS[display.locationTypes?.get(sensor.location) as SensorLocationType];
  const partType = partTypeKey ? display.translate(partTypeKey) : '';
  return { name, partType: partType && partType !== name ? partType : null };
}

// Front-to-back down the tenant grid, tires leading; unmapped locations sink to the end.
function sortKey(location: string, display: SensorDisplay): [number, number, number] {
  const pos = display.mapping?.find(l => l.location === location)?.position;
  const isTire = isTireLocationType(display.locationTypes?.get(location)) || !!tirePositionKey(location, display.assetType);
  return [isTire ? 0 : 1, pos?.[0] ?? Number.MAX_SAFE_INTEGER, pos?.[1] ?? 0];
}
